import React, { useEffect, useState, useRef } from 'react';
import LabelColumn from './LabelColumn';
import cn from 'classnames';
import UploadIcon from 'views/components/icons/Upload';
import { Mutations } from 'gql';
import { useMutation } from 'react-apollo';
import CloseIcon from 'views/components/icons/Close';
import generateOffset from '../NotesTab/generateOffset';
import imageCompression from 'browser-image-compression';
import { toast } from 'utils/Toast';
import Config from 'Config';

const LabelsTable = ({ phaseId, metro }) => {
  const [addCustomLabel] = useMutation(Mutations.ADD_CUSTOM_LABEL_FROM_FILE);
  const [addFetchedLabel] = useMutation(Mutations.ADD_CUSTOM_LABEL);
  const [updateCustomLabel] = useMutation(Mutations.UPDATE_CUSTOM_LABEL);
  const [updateCustomLabelImage] = useMutation(Mutations.UPDATE_CUSTOM_LABEL_IMAGE);
  const [deleteCustomLabel] = useMutation(Mutations.DELETE_CUSTOM_LABEL);
  const [newLabel, setNewLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [manualInput, setManualInput] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [options, setOptions] = useState();
  const [selectedImage, setSelectedImage] = useState();
  const [currentPicker, setCurrentPicker] = useState(null);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!newLabel || !manualInput) return;

    setLoading(true);
    clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(() => {
      const fetchData = async () => {
        try {
          const res = await fetch(`https://api.brandfetch.io/v2/search/${newLabel}?c=${Config.BRANDFETCH_KEY}`);
          const data = await res.json();
          setOptions(data);
          setDropdownOpen(true);
        } catch (err) {
          console.error('Error fetching:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, 300);

    return () => clearTimeout(debounceTimeout.current);
    // eslint-disable-next-line
  }, [newLabel]);

  const handleCurrentPicker = (id) => {
    setCurrentPicker(id);
  };

  const compressImgLabel = async (file) => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 200,
    };
    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error('Error compressing image:', error);
      return file;
    }
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Only jpeg, png, webp, and bmp images
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/bmp'].includes(selectedFile.type)) {
        toast('Please select a valid image file (jpeg, png, webp, bmp).', 'error');
        return;
      }
      // limit file size to 5MB
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast('Image size must be under 5MB.', 'error');
        return;
      }
      const compressedFile = await compressImgLabel(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1];
        const extension = reader.result.substring(reader.result.indexOf('/') + 1, reader.result.indexOf(';'));
        setSelectedImage({ img: base64String, extension });
      };
      reader.readAsDataURL(compressedFile);
    }
  };

  const onInputChange = (e) => {
    setManualInput(true);
    const value = e.target.value;
    setNewLabel(value);
  };

  const addNewCustomLabel = async () => {
    await addCustomLabel({
      variables: {
        phaseId,
        metroCode: metro.code,
        labelOffset: generateOffset(metro.customLabels.length),
        ...(!!newLabel && { value: newLabel }),
        ...(!!selectedImage && { imgBlob: selectedImage.img, fileExtension: selectedImage.extension }),
      },
    });
    setNewLabel('');
    setSelectedImage();
  };

  const addNewFetchedLabel = async () => {
    await addFetchedLabel({
      variables: {
        phaseId,
        metroCode: metro.code,
        labelOffset: generateOffset(metro.customLabels.length),
        ...(!!newLabel && { value: newLabel }),
        ...(!!selectedImage && { imgUrl: selectedImage.img }),
      },
    });
    setNewLabel('');
    setSelectedImage();
  };

  const deleteLabel = async (labelId) => {
    await deleteCustomLabel({
      variables: {
        phaseId,
        customLabelId: labelId,
      },
    });
  };

  const updateLabel = async (value) => {
    await updateCustomLabel({
      variables: {
        customLabelUpdateInput: { ...value, phaseId },
      },
    });
  };

  const updateLabelImage = async (label) => {
    await updateCustomLabelImage({
      variables: {
        phaseId,
        customLabelId: label.id,
        imgBlob: label.img,
        fileExtension: label.extension,
      },
    });
  };

  const removeFile = () => {
    setSelectedImage();
  };

  const onClickOption = (option) => {
    setManualInput(false);
    setNewLabel(option.name);
    const img = `https://cdn.brandfetch.io/${option.domain}/w/400/h/400?c=${Config.BRANDFETCH_KEY}`;
    setSelectedImage({ img, extension: 'link' });
    setOptions();
    setDropdownOpen(false);
  };

  return (
    <div className="table labels">
      {metro.customLabels?.map((label) => {
        const key = label.id.value;
        return (
          <LabelColumn
            key={key}
            formId={key}
            handleCurrentPicker={handleCurrentPicker}
            isPickerOpen={key === currentPicker}
            label={label}
            deleteLabel={deleteLabel}
            updateLabel={updateLabel}
            updateLabelImage={updateLabelImage}
          />
        );
      })}
      <div className="input-container">
        <div className={cn('column add-item', { 'no-table': !metro.customLabels.length })}>
          <div className="item first container">
            <div className="label icon-container clickable icon">
              {selectedImage ? (
                <div className="image-selected" title="Remove image" onClick={removeFile}>
                  <img
                    src={selectedImage.extension === 'link' ? selectedImage.img : `data:image/${selectedImage.extension};base64,${selectedImage.img}`}
                    alt="Label Icon"
                  />
                  <CloseIcon width={16} height={17} color="white" />
                </div>
              ) : (
                <div title="Add an image">
                  <UploadIcon width={16} height={17} color="#333333" />
                  <input type="file" accept=".jpg,.jpeg,.png,.webp,.bmp,image/jpeg,image/png,image/webp,image/bmp" onChange={handleFileChange} />
                </div>
              )}
            </div>
            <input className="add-input" value={newLabel} onChange={onInputChange} placeholder={'Type to add custom solution...'} />
          </div>
          <div className="item last infraComponent-item">
            <button
              className={cn('add-button', { disabled: !(newLabel || selectedImage) })}
              onClick={selectedImage?.extension === 'link' ? addNewFetchedLabel : addNewCustomLabel}>
              Add
            </button>
          </div>
        </div>
        {dropdownOpen && loading && !!newLabel && (
          <div className={'content-options no-result'}>
            <p>Loading...</p>
          </div>
        )}
        {dropdownOpen && options?.length === 0 && !loading && (
          <div className={'content-options no-result'}>
            <p>Solution could not be found. You can create a custom one</p>
          </div>
        )}
        {dropdownOpen && options?.length > 0 && !loading && (
          <div className="content-options">
            <ul>
              {options.map((option) => (
                <li key={`${option.brandId}`} onClick={() => onClickOption(option)}>
                  <img src={option.icon} alt={option.name} />
                  {option.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default LabelsTable;
